// CostPage.tsx — what the documentation costs, over a window the reader picks.
//
// Three cuts of one ledger, and each answers a different question. The figures
// across the top are "what has this window cost". By model is "what the
// overflow switch costs" — the whole reason two models exist is that the local
// one is effectively free and OpenRouter is not, so a window where the hosted
// model dominates the spend is the queue having been deep for a while. By repo
// is "which repository is expensive", which is the actionable one: a repo that
// churns generated files re-documents them on every push. The curve underneath
// is whether any of it is getting worse.
import { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { BsArrowDownCircle, BsArrowUpCircle, BsCash, BsChatLeftText } from 'react-icons/bs';
import Dropdown from 'common/ui/Dropdown';
import { fetchCost } from '../api';
import type { CostPoint, CostSummary } from '../api';
import { formatCount, formatUsd, percent } from '../format';
import Metric from '../components/Metric';

// The windows worth having. A day is "what is happening now", a week is the
// billing-shaped unit, a month is the trend — anything finer than a day is
// better read off the status route's live counters.
const WINDOWS = [
  { value: '24', label: 'Last 24 hours' },
  { value: '168', label: 'Last 7 days' },
  { value: '720', label: 'Last 30 days' },
];

export default function CostPage() {
  const [hours, setHours] = useState('24');
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true);
    void (async () => {
      try {
        const next = await fetchCost(Number(hours));
        if (!live) return;
        setSummary(next);
        setError('');
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : 'Failed to load the cost ledger');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [hours]);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h1 className="m-0">Cost</h1>
        {/* The window control sits with the title, not inside a card: it
            governs every card below, and a control inside one of them would
            read as belonging to that one. */}
        <Dropdown
          value={hours}
          options={WINDOWS}
          aria-label="Cost window"
          onChange={(e) => setHours(e.target.value)}
        />
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading && !summary ? (
        <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
      ) : summary ? (
        <>
          <div className="card-grid mb-4">
            <Metric
              icon={<BsCash className="text-success" />}
              label="Spend"
              value={formatUsd(summary.cost_usd)}
              note={`Over the last ${summary.window_hours} hour${summary.window_hours === 1 ? '' : 's'}.`}
            />
            <Metric
              icon={<BsChatLeftText className="text-primary" />}
              label="Requests"
              value={formatCount(summary.requests)}
              note="One per file or directory summarised, retries included."
            />
            <Metric
              icon={<BsArrowUpCircle className="text-info" />}
              label="Prompt tokens"
              value={formatCount(summary.prompt_tokens)}
              note="Source text and child summaries sent to the model."
            />
            <Metric
              icon={<BsArrowDownCircle className="text-warning" />}
              label="Completion tokens"
              value={formatCount(summary.completion_tokens)}
              note="The documentation itself, as written."
            />
          </div>

          <SpendChart series={summary.series} totalUsd={summary.cost_usd} hours={summary.window_hours} />

          <Row className="g-3">
            <Col xl={6}>
              <ModelBreakdown summary={summary} />
            </Col>
            <Col xl={6}>
              <RepoBreakdown summary={summary} />
            </Col>
          </Row>
        </>
      ) : null}
    </div>
  );
}

interface TooltipPayloadItem { payload: CostPoint }

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

function ChartTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  return (
    <div className="p-2 small" style={{
      background: 'var(--bs-body-bg)',
      border: '1px solid var(--bs-border-color)',
      borderRadius: 'var(--bs-border-radius, 0.375rem)',
    }}>
      <div className="fw-semibold">{new Date(point.ts).toLocaleString()}</div>
      <div>{formatUsd(point.cost_usd)}</div>
      <div className="text-muted">{formatCount(point.tokens)} tokens</div>
    </div>
  );
}

// SpendChart draws the series the server bucketed. The y-axis starts at zero
// and is money: a truncated axis is the standard way a cost chart understates
// a spike, and this is the one chart in the app somebody will screenshot at a
// budget conversation.
//
// Every colour is a --bs-* token, so the chart follows the theme toggle and any
// re-solved palette without a second set of values to keep in sync.
function SpendChart({ series, totalUsd, hours }: { series: CostPoint[]; totalUsd: number; hours: number }) {
  // Within a day the buckets are hours and the date is noise; past that the
  // hour is noise and the date is what distinguishes them.
  const tickLabel = (ts: string) => {
    const date = new Date(ts);
    if (Number.isNaN(date.getTime())) return ts;
    return hours <= 24
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
  };
  const peak = series.reduce((max, p) => Math.max(max, p.cost_usd), 0);

  return (
    <Card className="mb-4">
      <Card.Header>Spend Over Time</Card.Header>
      <Card.Body>
        {series.length < 2 ? (
          <p className="text-muted small m-0">
            Not enough history in this window to plot — the curve needs at least two buckets.
          </p>
        ) : (
          // Height covers the plot and the x-axis band together, so the tick
          // labels can't fall outside the card and give it a nested scrollbar.
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series} margin={{ top: 16, right: 24, left: 0, bottom: 0 }}>
              {/* Solid hairlines, one shade off the surface: a dashed grid
                  reads as a threshold when it is only a grid. */}
              <CartesianGrid stroke="var(--bs-border-color)" strokeOpacity={0.5} vertical={false} />
              <XAxis dataKey="ts" tickFormatter={tickLabel} fontSize={11} minTickGap={28}
                stroke="var(--bs-border-color)" tick={{ fill: 'var(--bs-secondary-color)' }} />
              <YAxis width={64} fontSize={11} domain={[0, 'auto']} tickFormatter={formatUsd}
                stroke="var(--bs-border-color)" tick={{ fill: 'var(--bs-secondary-color)' }} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'var(--bs-border-color)' }} />
              {/* Filled, unlike the line charts elsewhere in the estate: this
                  is an accumulating quantity, and the area under it is the
                  bill — the shape people already read money as. */}
              <Area type="monotone" dataKey="cost_usd" stroke="var(--bs-primary)" strokeWidth={2}
                fill="var(--bs-primary)" fillOpacity={0.18} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card.Body>
      <Card.Footer className="small text-muted">
        {formatUsd(totalUsd)} over {series.length} bucket{series.length === 1 ? '' : 's'}
        {peak > 0 && <> · busiest bucket {formatUsd(peak)}</>}
      </Card.Footer>
    </Card>
  );
}

// ModelBreakdown is the overflow switch, priced. The share column is what makes
// it worth a card: the hosted model is a small fraction of the requests and can
// be most of the money, and two absolute numbers side by side do not say that.
function ModelBreakdown({ summary }: { summary: CostSummary }) {
  const rows = [...summary.by_model].sort((a, b) => b.cost_usd - a.cost_usd);
  return (
    <Card className="h-100">
      <Card.Header>By Model</Card.Header>
      <Card.Body>
        {rows.length === 0 ? (
          <p className="text-muted small m-0">No model calls in this window.</p>
        ) : (
          <Table size="sm" hover responsive className="m-0">
            <thead>
              <tr>
                <th>Model</th>
                <th className="text-end">Requests</th>
                <th className="text-end">Tokens</th>
                <th className="text-end">Cost</th>
                <th className="text-end">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.model}>
                  <td className="align-middle"><code className="small">{row.model}</code></td>
                  <td className="align-middle text-end small">{formatCount(row.requests)}</td>
                  <td className="align-middle text-end small text-muted"
                    title="Prompt → completion tokens">
                    {formatCount(row.prompt_tokens)} → {formatCount(row.completion_tokens)}
                  </td>
                  <td className="align-middle text-end small">{formatUsd(row.cost_usd)}</td>
                  <td className="align-middle text-end small text-muted">
                    {percent(row.cost_usd, summary.cost_usd)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
      <Card.Footer className="small text-muted">
        The local model is effectively free; the overflow model is billed per token.
      </Card.Footer>
    </Card>
  );
}

// RepoBreakdown is the actionable cut: a repository near the top of this table
// with few runs is documenting a lot per push, which usually means generated
// files are being summarised.
function RepoBreakdown({ summary }: { summary: CostSummary }) {
  const rows = [...summary.by_repo].sort((a, b) => b.cost_usd - a.cost_usd);
  return (
    <Card className="h-100">
      <Card.Header>By Repository</Card.Header>
      <Card.Body>
        {rows.length === 0 ? (
          <p className="text-muted small m-0">No runs in this window.</p>
        ) : (
          <Table size="sm" hover responsive className="m-0">
            <thead>
              <tr>
                <th>Repository</th>
                <th className="text-end">Runs</th>
                <th className="text-end">Tokens</th>
                <th className="text-end">Cost</th>
                <th className="text-end">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.repo_full_name}>
                  <td className="align-middle text-truncate">{row.repo_full_name}</td>
                  <td className="align-middle text-end small">{row.runs}</td>
                  <td className="align-middle text-end small text-muted"
                    title="Prompt → completion tokens">
                    {formatCount(row.prompt_tokens)} → {formatCount(row.completion_tokens)}
                  </td>
                  <td className="align-middle text-end small">{formatUsd(row.cost_usd)}</td>
                  <td className="align-middle text-end small text-muted">
                    {percent(row.cost_usd, summary.cost_usd)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
      <Card.Footer className="small text-muted">
        Cost is attributed to the repository whose run made the call, retries included.
      </Card.Footer>
    </Card>
  );
}
